import type { ReactNode } from 'react';
import { Button } from './Button';
import { Card } from './Card';
import { PageHeader } from './PageHeader';
import { Stepper } from './Stepper';

/**
 * Shared chrome for the onboarding wizards (mockup module 08): a centered
 * (max-width 920px) column of header → `Stepper` → body card → nav footer.
 *
 * - **header**: breadcrumb + title + subtitle on the left, a "Cancelar" button
 *   (`x` → `onCancel`) on the right.
 * - **body**: the current step's `children` inside a padded `Card`.
 * - **footer**: "Voltar" (`arrow-left`, left, `disabled={backDisabled}`) and the
 *   primary action on the right — `isLast ? "Finalizar cadastro" (check-circle)
 *   : "Continuar" (arrow-right)`, disabled while `nextDisabled || submitting`.
 *
 * Layout-only: it owns no step state. The wizard page drives `current`, the
 * disabled flags and the three callbacks (consumed in fatia 2b-ii).
 */
export function WizardShell({
  breadcrumb,
  title,
  subtitle,
  steps,
  current,
  onCancel,
  onBack,
  onNext,
  isLast = false,
  backDisabled = false,
  nextDisabled = false,
  submitting = false,
  children,
}: {
  breadcrumb?: string[];
  title: string;
  subtitle?: string;
  steps: string[];
  current: number;
  onCancel: () => void;
  onBack: () => void;
  onNext: () => void;
  isLast?: boolean;
  backDisabled?: boolean;
  nextDisabled?: boolean;
  submitting?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="mx-auto max-w-[920px]">
      <PageHeader
        title={title}
        {...(breadcrumb ? { breadcrumb } : {})}
        {...(subtitle ? { subtitle } : {})}
        actions={
          <Button variant="secondary" icon="x" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
        }
      />

      <Stepper steps={steps} current={current} />

      <Card className="p-8">{children}</Card>

      <div className="mt-5 flex justify-between">
        <Button variant="secondary" icon="arrow-left" onClick={onBack} disabled={backDisabled}>
          Voltar
        </Button>
        {isLast ? (
          <Button variant="primary" icon="check-circle" onClick={onNext} disabled={nextDisabled || submitting}>
            Finalizar cadastro
          </Button>
        ) : (
          <Button variant="primary" icon="arrow-right" onClick={onNext} disabled={nextDisabled || submitting}>
            Continuar
          </Button>
        )}
      </div>
    </div>
  );
}
